import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Compass, Sparkles, Sunrise, UserCog } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../lib/auth';
import { preferencesAPI } from '../lib/api';

/**
 * Personal preferences. Nothing here touches another user or an
 * organization — these are this account's own switches.
 *
 *   • Daily Business Review  — opt-out field, so `!== false` means ON.
 *   • Show Operational Base  — super-admin-only personal visibility
 *     switch, default OFF. Clients are governed by the per-org switch.
 */
const PrefRow = ({ icon: Icon, label, description, checked, onChange, saving, testId }) => (
    <div className="flex items-start justify-between gap-6 p-4 rounded-lg border border-slate-200 dark:border-slate-700"
         data-testid={`${testId}-row`}>
        <div className="flex-1">
            <Label className="text-sm font-semibold flex items-center gap-2">
                <Icon className="w-4 h-4 text-emerald-500" />
                {label}
            </Label>
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{description}</p>
        </div>
        <Switch checked={checked} disabled={saving} onCheckedChange={onChange} data-testid={testId} />
    </div>
);

export default function MySettings() {
    const { t } = useTranslation();
    const { user, patchUser, isSuperAdmin } = useAuth();
    const [saving, setSaving] = useState(false);

    const dailyReview = user?.daily_review_enabled !== false;
    const showOb = user?.show_operational_base === true;
    const showTour = user?.show_guided_tour !== false;

    const save = async (fields) => {
        setSaving(true);
        try {
            await preferencesAPI.update(fields);
            patchUser(fields);
            toast.success(t('mySettings.saved'));
        } catch {
            toast.error(t('common.saveFailed', 'Could not save'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-3xl space-y-6" data-testid="my-settings-page">
            <header className="space-y-1">
                <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-900 dark:text-white">
                    <UserCog className="w-6 h-6 text-rose-500" />
                    {t('mySettings.title')}
                </h1>
                <p className="text-sm text-muted-foreground">{t('mySettings.subtitle')}</p>
            </header>

            <Card className="p-5 space-y-3">
                <PrefRow
                    icon={Sunrise}
                    label={t('mySettings.dailyReview.label')}
                    description={t('mySettings.dailyReview.description')}
                    checked={dailyReview}
                    saving={saving}
                    onChange={(next) => save({ daily_review_enabled: next })}
                    testId="my-settings-daily-review-toggle"
                />

                <PrefRow
                    icon={Compass}
                    label={t('mySettings.guidedTour.label', 'Show guided tour')}
                    description={t('mySettings.guidedTour.description', 'Turn the short walkthrough back on. You can always start it from the tour button.')}
                    checked={showTour}
                    saving={saving}
                    onChange={(next) => save({ show_guided_tour: next })}
                    testId="my-settings-guided-tour-toggle"
                />

                {isSuperAdmin() && (
                    <PrefRow
                        icon={Sparkles}
                        label={t('mySettings.showOperationalBase.label')}
                        description={t('mySettings.showOperationalBase.description')}
                        checked={showOb}
                        saving={saving}
                        onChange={(next) => save({ show_operational_base: next })}
                        testId="my-settings-show-ob-toggle"
                    />
                )}
            </Card>
        </div>
    );
}
